/**
 * SSE (Server-Sent Events) streaming client for the Atlas chat Lambda Function URL.
 *
 * Parses the backend SSE protocol defined in packages/brain/src/streaming/response.ts:
 * - text: streamed AI response content
 * - tool_start: tool execution started
 * - tool_end: tool execution completed with result
 * - error: error message
 * - done: stream terminator (data: [DONE])
 */

import type { SSEEvent, ChatRequestBody } from "@atlas/shared";
import { SSE_DONE_SENTINEL } from "@atlas/shared";

// Re-export shared type for consumers
export type { SSEEvent };

/**
 * Callbacks for handling SSE events during streaming
 */
export interface SSECallbacks {
  onText: (text: string) => void;
  onToolStart: (toolName: string) => void;
  onToolEnd: (toolName: string, result: string) => void;
  onError: (message: string) => void;
  onDone: () => void;
}

/**
 * Streams a chat request to the Lambda Function URL and parses SSE events.
 *
 * Uses native fetch with ReadableStream -- no EventSource (which only supports GET).
 * Buffers incoming text and splits on \n\n boundaries to handle TCP chunking.
 *
 * @param chatUrl - Lambda Function URL endpoint
 * @param messages - Conversation history to send
 * @param token - Auth token (Cognito access token)
 * @param callbacks - Event handlers for each SSE event type
 * @param signal - Optional AbortSignal for cancellation
 */
export async function streamChat(
  chatUrl: string,
  messages: Array<{ role: string; content: string }>,
  token: string,
  callbacks: SSECallbacks,
  signal?: AbortSignal,
): Promise<void> {
  // Filter out messages with empty content to avoid 400 from backend validation
  // (e.g. assistant placeholder messages from aborted/failed streams)
  const validMessages = messages.filter((m) => m.content.length > 0);

  const body: ChatRequestBody = {
    message: validMessages[validMessages.length - 1]?.content ?? "",
    conversationHistory: validMessages.slice(0, -1) as ChatRequestBody["conversationHistory"],
  };

  const response = await fetch(chatUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Chat request failed: ${response.status}`);
  }

  if (!response.body) {
    throw new Error("Response body is null");
  }

  const reader = response.body
    .pipeThrough(new TextDecoderStream())
    .getReader();

  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += value;
      const chunks = buffer.split("\n\n");
      buffer = chunks.pop() ?? ""; // Keep trailing incomplete chunk

      for (const chunk of chunks) {
        if (!chunk.startsWith("data: ")) continue;
        const payload = chunk.slice(6); // Strip "data: " prefix

        if (payload === SSE_DONE_SENTINEL) {
          callbacks.onDone();
          return;
        }

        try {
          const event: SSEEvent = JSON.parse(payload);
          switch (event.type) {
            case "text":
              callbacks.onText(event.data ?? "");
              break;
            case "tool_start":
              callbacks.onToolStart(event.toolName ?? "");
              break;
            case "tool_end":
              callbacks.onToolEnd(event.toolName ?? "", event.data ?? "");
              break;
            case "error":
              callbacks.onError(event.data ?? "Unknown error");
              break;
          }
        } catch {
          // Skip malformed JSON events silently
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
